// AIA (Meta Automotive Inventory Ads) pre-publish checklist.
// -----------------------------------------------------------------------------
// Mirrors the field-mapping logic in `src/routes/feeds/facebook.ts` so admins
// see — *before* a car is saved with status="active" — exactly which fields
// will block ingestion (errors) and which will degrade targeting (warnings).
//
// Keep this file in sync with facebook.ts. The duplication is intentional:
// the feed must run server-side at the edge with no React/UI deps, and this
// validator must run in the admin dialog without pulling in supabase clients.
//
// Surface area covered:
//  • Required fields  → vehicle_id, title, make, model, year, mileage, price,
//                       images, address components, dealer geo.
//  • Identifiers     → VIN (recommended), trim, GTIN format check.
//  • Enum mappings   → fuel_type / transmission / body_style. We warn when the
//                      raw source value falls back to "OTHER" because that
//                      kills delivery in fuel/body-targeted ads.
//  • Mileage unit    → integer, non-negative, sane upper bound.
//  • Address         → street, city, postal_code, country (from CONTACT).

import { CONTACT } from "@/config/contact";

// --- Re-implemented enum maps (must match feeds/facebook.ts) ----------------
const FUEL_MAP: Record<string, string> = {
  benzin: "GASOLINE", petrol: "GASOLINE", gasoline: "GASOLINE",
  diesel: "DIESEL",
  el: "ELECTRIC", electric: "ELECTRIC", ev: "ELECTRIC",
  hybrid: "HYBRID",
  plugin_hybrid: "PLUGIN_HYBRID_ELECTRIC",
  "plugin-hybrid": "PLUGIN_HYBRID_ELECTRIC",
  phev: "PLUGIN_HYBRID_ELECTRIC",
  flex: "FLEX", cng: "CNG", lpg: "LPG", hydrogen: "HYDROGEN",
};

const TRANSMISSION_MAP: Record<string, string> = {
  automatik: "AUTOMATIC", automatic: "AUTOMATIC", auto: "AUTOMATIC",
  manuel: "MANUAL", manual: "MANUAL",
};

function mapBodyStyle(raw: string | null | undefined): string {
  if (!raw) return "OTHER";
  const s = raw.toLowerCase();
  if (s.includes("cabrio") || s.includes("convertible") || s.includes("roadster")) return "CONVERTIBLE";
  if (s.includes("coupe") || s.includes("coupé")) return "COUPE";
  if (s.includes("halvkombi") || s.includes("hatch") || s.includes("mikro")) return "HATCHBACK";
  if (s.includes("minivan") || s.includes("mpv")) return "MINIVAN";
  if (s.includes("pickup") || s.includes("truck")) return "TRUCK";
  if (s.includes("suv")) return "SUV";
  if (s.includes("crossover") || s.includes("cuv")) return "CROSSOVER";
  if (s.includes("sedan") || s.includes("limousine")) return "SEDAN";
  if (s.includes("varevogn") || s.includes("van") || s.includes("kassebil")) return "VAN";
  if (s.includes("stationcar") || s.includes("wagon") || s.includes("estate")) return "WAGON";
  return "OTHER";
}

const VALID_GTIN_LENGTHS = new Set([8, 12, 13, 14]);

// Sanity ceiling — any car listed with > 1.5M km is almost certainly a typo
// and Meta will likely reject it as implausible.
const MILEAGE_MAX_PLAUSIBLE = 1_500_000;

export type AiaCheckSeverity = "error" | "warning" | "ok";

export type AiaCheckCategory =
  | "required"
  | "identifiers"
  | "mapping"
  | "mileage"
  | "address";

export interface AiaCheckItem {
  /** Stable id so React keys + tests don't depend on label strings. */
  id: string;
  category: AiaCheckCategory;
  /** Short human label shown in the checklist row. */
  label: string;
  severity: AiaCheckSeverity;
  /** Optional one-line explanation of the result. */
  detail?: string;
}

export interface AiaChecklistResult {
  items: AiaCheckItem[];
  errorCount: number;
  warningCount: number;
  /** Convenience: true when the car is publish-ready (no errors). */
  ready: boolean;
}

export interface AiaCarInput {
  brand: string | null | undefined;
  model: string | null | undefined;
  variant: string | null | undefined;
  year: number | null | undefined;
  mileage: number | null | undefined;
  price: number | null | undefined;
  fuel_type: string | null | undefined;
  transmission: string | null | undefined;
  body_type: string | null | undefined;
  color?: string | null | undefined;
  images: string[] | null | undefined;
  vin_number?: string | null | undefined;
  gtin?: string | null | undefined;
}

export function checkAiaReadiness(car: AiaCarInput): AiaChecklistResult {
  const items: AiaCheckItem[] = [];

  // ---- Required core fields -------------------------------------------------
  items.push(field("brand", "Mærke (make)", car.brand, "required"));
  items.push(field("model", "Model", car.model, "required"));
  items.push(
    car.year && car.year >= 1900 && car.year <= new Date().getFullYear() + 1
      ? ok("year", "Modelår", "required", `${car.year}`)
      : err("year", "Modelår", "required", "Mangler eller ugyldigt år"),
  );
  items.push(
    typeof car.price === "number" && car.price > 0
      ? ok("price", "Pris (DKK)", "required")
      : err("price", "Pris (DKK)", "required", "Pris skal være > 0"),
  );

  // Images: at least one URL required; warn if any are obviously broken.
  const images = (car.images ?? []).filter(Boolean);
  if (images.length === 0) {
    items.push(err("images", "Billeder", "required", "Mindst ét billede påkrævet"));
  } else {
    const bad = images.filter((u) => !u.startsWith("http") || /\s/.test(u));
    if (bad.length === images.length) {
      items.push(err("images", "Billeder", "required", `Alle ${images.length} URL'er ser invalide ud`));
    } else if (bad.length > 0) {
      items.push(warn("images", "Billeder", "required", `${bad.length}/${images.length} URL'er ser invalide ud`));
    } else {
      items.push(ok("images", "Billeder", "required", `${images.length} stk.`));
    }
  }

  // ---- Mileage (value + unit formatting) -----------------------------------
  // The feed always emits unit=KM; we only need to validate the numeric value
  // is a non-negative integer within plausible bounds.
  if (car.mileage === null || car.mileage === undefined) {
    items.push(err("mileage", "Kilometerstand", "mileage", "Mangler — feed bruger <value>/<unit>=KM"));
  } else if (!Number.isInteger(car.mileage)) {
    items.push(err("mileage", "Kilometerstand", "mileage", "Skal være et heltal (KM uden decimaler)"));
  } else if (car.mileage < 0) {
    items.push(err("mileage", "Kilometerstand", "mileage", "Negativ km-stand er ikke gyldig"));
  } else if (car.mileage > MILEAGE_MAX_PLAUSIBLE) {
    items.push(warn(
      "mileage",
      "Kilometerstand",
      "mileage",
      `${car.mileage.toLocaleString("da-DK")} km er over plausibilitetsgrænsen — Meta kan afvise`,
    ));
  } else {
    items.push(ok("mileage", "Kilometerstand", "mileage", `${car.mileage.toLocaleString("da-DK")} km`));
  }

  // ---- Identifiers (VIN, trim, GTIN) ---------------------------------------
  // VIN is technically optional but highly recommended for matching against
  // Meta's catalog and reducing duplicate-listing penalties.
  if (!car.vin_number?.trim()) {
    items.push(warn("vin", "Stelnummer (VIN)", "identifiers", "Anbefalet — bruges til catalog matching"));
  } else if (car.vin_number.trim().length !== 17) {
    items.push(warn(
      "vin",
      "Stelnummer (VIN)",
      "identifiers",
      `VIN er ${car.vin_number.trim().length} tegn — moderne VIN er typisk 17`,
    ));
  } else {
    items.push(ok("vin", "Stelnummer (VIN)", "identifiers"));
  }

  // <trim> in the feed maps from car.variant. Optional, but missing trim
  // hurts ad relevance for variant-targeted searches.
  if (!car.variant?.trim()) {
    items.push(warn("trim", "Variant (trim)", "identifiers", "Mangler — annoncer mister variant-targeting"));
  } else {
    items.push(ok("trim", "Variant (trim)", "identifiers", car.variant));
  }

  if (car.gtin?.trim()) {
    const g = car.gtin.trim();
    if (!/^\d+$/.test(g) || !VALID_GTIN_LENGTHS.has(g.length)) {
      items.push(warn(
        "gtin",
        "GTIN",
        "identifiers",
        `Ugyldigt format (skal være 8/12/13/14 cifre) — feedet vil udelade værdien`,
      ));
    } else {
      items.push(ok("gtin", "GTIN", "identifiers"));
    }
  }
  // No GTIN is fine — biler har sjældent GTIN. Vis ikke en advarsel.

  // ---- Enum mappings (fuel/transmission/body_style) -------------------------
  // The feed always emits *something* (OTHER fallback) so it never hard-blocks
  // ingestion, but OTHER kills targeted-delivery — flag as warnings.
  const fuelKey = (car.fuel_type ?? "").toLowerCase().trim();
  const fuelMapped = FUEL_MAP[fuelKey];
  if (!car.fuel_type?.trim()) {
    items.push(warn("fuel_type", "Brændstof → fuel_type", "mapping", "Mangler — feed sender OTHER"));
  } else if (!fuelMapped) {
    items.push(warn(
      "fuel_type",
      "Brændstof → fuel_type",
      "mapping",
      `"${car.fuel_type}" matcher ingen Meta-enum → falder tilbage til OTHER`,
    ));
  } else {
    items.push(ok("fuel_type", "Brændstof → fuel_type", "mapping", fuelMapped));
  }

  const transKey = (car.transmission ?? "").toLowerCase().trim();
  const transMapped = TRANSMISSION_MAP[transKey];
  if (!car.transmission?.trim()) {
    items.push(warn("transmission", "Gearkasse → transmission", "mapping", "Mangler — feed sender OTHER"));
  } else if (!transMapped) {
    items.push(warn(
      "transmission",
      "Gearkasse → transmission",
      "mapping",
      `"${car.transmission}" matcher ingen Meta-enum → falder tilbage til OTHER`,
    ));
  } else {
    items.push(ok("transmission", "Gearkasse → transmission", "mapping", transMapped));
  }

  const bodyMapped = mapBodyStyle(car.body_type);
  if (!car.body_type?.trim()) {
    items.push(warn("body_style", "Karrosseri → body_style", "mapping", "Mangler — feed sender OTHER"));
  } else if (bodyMapped === "OTHER") {
    items.push(warn(
      "body_style",
      "Karrosseri → body_style",
      "mapping",
      `"${car.body_type}" matcher ingen kendt karrosseri-type → OTHER`,
    ));
  } else {
    items.push(ok("body_style", "Karrosseri → body_style", "mapping", bodyMapped));
  }

  // ---- Address components (from CONTACT config) ----------------------------
  // These are dealership-level (not per-car) but the feed embeds them per
  // listing, so a missing CONTACT field affects every row in the feed.
  const addr = CONTACT.address;
  const addrParts: Array<{ key: string; label: string; value: string | null | undefined }> = [
    { key: "street", label: "Adresse: gade", value: addr.street },
    { key: "city", label: "Adresse: by", value: addr.city },
    { key: "postal_code", label: "Adresse: postnummer", value: addr.postalCode },
    { key: "country", label: "Adresse: land (ISO)", value: addr.country },
  ];
  for (const p of addrParts) {
    if (!p.value || !String(p.value).trim()) {
      items.push(err(`addr_${p.key}`, p.label, "address", "Mangler i CONTACT-konfig"));
    } else {
      items.push(ok(`addr_${p.key}`, p.label, "address", String(p.value)));
    }
  }
  // Geo is optional in the feed (we omit when missing) but worth surfacing.
  if (typeof addr.latitude !== "number" || typeof addr.longitude !== "number") {
    items.push(warn(
      "geo",
      "Adresse: lat/long",
      "address",
      "Mangler i CONTACT — distance-targeting er deaktiveret",
    ));
  } else {
    items.push(ok("geo", "Adresse: lat/long", "address", `${addr.latitude}, ${addr.longitude}`));
  }

  const errorCount = items.filter((i) => i.severity === "error").length;
  const warningCount = items.filter((i) => i.severity === "warning").length;
  return { items, errorCount, warningCount, ready: errorCount === 0 };
}

// ---- internal helpers ------------------------------------------------------
function field(
  id: string,
  label: string,
  value: string | null | undefined,
  category: AiaCheckCategory,
): AiaCheckItem {
  return value && value.trim()
    ? ok(id, label, category)
    : err(id, label, category, "Mangler");
}
function ok(id: string, label: string, category: AiaCheckCategory, detail?: string): AiaCheckItem {
  return { id, label, category, severity: "ok", detail };
}
function warn(id: string, label: string, category: AiaCheckCategory, detail?: string): AiaCheckItem {
  return { id, label, category, severity: "warning", detail };
}
function err(id: string, label: string, category: AiaCheckCategory, detail?: string): AiaCheckItem {
  return { id, label, category, severity: "error", detail };
}
